/**
 * RaoLanding — the RAO dashboard.
 *
 * Lists the officer's in-flight carts via `v3.carts.listMy()` plus a single
 * "New application" CTA.  /v3/carts/my is implemented on the Function side
 * (CartFunction.ListMy) and resolves the officer from the x-pcb-officer-code
 * header that ApiClientContext injects from AuthContext — no client-side
 * filter required.
 *
 * Card UX (Bill, 2026-04-24):
 *   - Cards surface applicant name, stage, products, branch, officer,
 *     "X min ago" — never a Cart ID, GUID, or raw 2-letter code.
 *   - The grid is `auto-fill, minmax(320px, 1fr)` so it adapts from 4-up on
 *     a wide desktop down to 1-up on phones with no breakpoint plumbing.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type {
  CartDto,
  CartResolutionCode,
  SweepEmptyFsrDraftsResponse,
} from '@retail-ops/shared-types';
import { Button } from '@/components/primitives';
import { Toast, type ToastVariant } from '@/components/common/Toast';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import { useApi } from './hooks/useApi';
import {
  InFlightCartCardSkeleton,
  InFlightCartGrid,
} from './components/InFlightCartCard';
import { InFlightCartRow, InFlightCartList } from './components/InFlightCartRow';
import { identifyCart } from '@/utils/cartLabel';
import { CloseCartModal } from './CloseCartModal';
import { CartStateService } from '@/services/CartStateService';
import { SweepEmptiesPopover } from './components/SweepEmptiesPopover';
import { OnlineApplicationsPanel } from './components/OnlineApplicationsPanel';

type ListState =
  | { kind: 'loading' }
  | { kind: 'ok'; carts: ReadonlyArray<CartDto> }
  | { kind: 'error'; message: string };

export function RaoLanding(): JSX.Element {
  const { client, makeSignal } = useApi();
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useAuth();

  const [state, setState] = useState<ListState>({ kind: 'loading' });
  const [closeTarget, setCloseTarget] = useState<CartDto | null>(null);
  const [reassignTarget, setReassignTarget] = useState<CartDto | null>(null);
  const [reassignTo, setReassignTo] = useState<string>('');
  const [reassignBusy, setReassignBusy] = useState<boolean>(false);
  const [reassignErr, setReassignErr] = useState<string>('');
  const [officers, setOfficers] = useState<ReadonlyArray<{ officerCode: string; displayName: string; branchCode?: string }>>([]);
  const [sweepOpen, setSweepOpen] = useState<boolean>(false);
  const [toast, setToast] = useState<{ variant: ToastVariant; message: string } | null>(null);

  useEffect(() => {
    const signal = makeSignal();
    client.retail.carts.listMy(undefined, { signal }).then(
      (res): void => {
        const anyRes = res as unknown as { success?: boolean; data?: CartDto[]; error?: string };
        if (anyRes?.success === false) {
          setState({ kind: 'error', message: anyRes.error ?? 'Unable to load carts.' });
          return;
        }
        const carts = (anyRes.data ?? []) as ReadonlyArray<CartDto>;
        setState({ kind: 'ok', carts });
      },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Unable to load carts.',
        });
      },
    );
    // client + makeSignal are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Defense-in-depth filter: never surface a cart with a non-null
  // ResolutionCode as if it were "in-progress". Server-side /v3/carts/my
  // is the source of truth (Wave 6.2 backend fix excludes resolved rows),
  // but if the API regresses or the cache lags we still don't want to
  // render Cancelled / Adverse-Action / Processed carts on the open list.
  const openCarts =
    state.kind === 'ok'
      ? state.carts.filter((c) => !c.resolutionCode)
      : [];

  const sortedCarts =
    state.kind === 'ok'
      ? [...openCarts].sort((a, b) => {
          const tA = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
          const tB = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
          return tB - tA;
        })
      : [];

  const handleOpen = (cart: CartDto): void => { void navigate(`/rao/${cart.cartId}`); };

  // BACKLOG-1746 - recently committed applications (server: /v3/carts/my?status=committed).
  const [recent, setRecent] = useState<ReadonlyArray<CartDto>>([]);
  useEffect(() => {
    const ctrl = new AbortController();
    client.retail.carts.listMy({ status: 'committed', top: 10 }, { signal: ctrl.signal })
      .then((res) => { if (res.success && res.data) setRecent(res.data); })
      .catch(() => { /* the section simply stays hidden */ });
    return () => ctrl.abort();
  }, [client]);

  const handleCloseCart = useCallback((cart: CartDto): void => {
    // Bill 2026-09-11: "Closing an empty cart should not need a confirmation
    // or reason." Nothing to attest to on a cart with no person and no
    // product - discard it straight away (the server purges the row tree).
    const id = identifyCart(cart.peopleJson, cart.productsJson);
    if (!id.who && !id.what) {
      void (async () => {
        try {
          await client.retail.carts.archive(cart.cartId, { reason: 'Cancelled', details: 'Empty cart discarded from the New Acct list' });
          setState((prev) =>
            prev.kind === 'ok'
              ? { kind: 'ok', carts: prev.carts.filter((c) => c.cartId !== cart.cartId) }
              : prev,
          );
          setToast({ variant: 'info', message: 'Empty cart discarded.' });
        } catch {
          setToast({ variant: 'warn', message: 'Could not discard the empty cart - try again.' });
        }
      })();
      return;
    }
    setCloseTarget(cart);
  }, [client]);

  // ── Reassign (Bill 2026-07-27) ──────────────────────────────────────────
  // Supervisory hand-off of a cart to another officer. The SERVER is the real
  // gate (Supervisor / Manager / Admin / SuperAdmin) and it derives the
  // receiving officer's officer code AND branch from their provisioned record.
  // This client check only avoids offering an action that would 403.
  const canReassign = ['Supervisor', 'Manager', 'Admin', 'SuperAdmin'].some(
    (r) => (user?.roles ?? []).some((ur) => ur.toLowerCase() === r.toLowerCase()),
  );
  const handleReassign = useCallback((cart: CartDto): void => {
    setReassignTarget(cart);
    setReassignTo('');
    setReassignErr('');
    // Load the picker list. A free-text officer code could be mistyped and only
    // failed at submit (Bill hit exactly that on the first real use).
    void (async () => {
      try {
        const res = await client.retail.carts.officers();
        if (res?.success && Array.isArray(res.officers)) {
          setOfficers(res.officers.filter((o) => o.officerCode !== user?.officerCode));
        }
      } catch { /* picker falls back to an empty list; the dialog still explains */ }
    })();
  }, [client, user?.officerCode]);

  const submitReassign = useCallback(async (): Promise<void> => {
    if (!reassignTarget || !reassignTo.trim()) return;
    setReassignBusy(true);
    setReassignErr('');
    try {
      const svc = new CartStateService(client);
      const res = await svc.reassign(reassignTarget.cartId, reassignTo.trim(), 'Handoff from RAO landing');
      if (!res.success) {
        setReassignErr(res.error ?? 'Reassign failed.');
        return;
      }
      // Drop the cart from MY list — it now belongs to the receiving officer.
      setState((prev) =>
        prev.kind === 'ok'
          ? { ...prev, carts: prev.carts.filter((c: CartDto) => c.cartId !== reassignTarget.cartId) }
          : prev,
      );
      setReassignTarget(null);
    } catch (err) {
      setReassignErr(err instanceof Error ? err.message : String(err));
    } finally {
      setReassignBusy(false);
    }
  }, [reassignTarget, reassignTo, client]);

  const handleResolved = useCallback(
    (resolved: CartDto, code: CartResolutionCode): void => {
      setState((prev) =>
        prev.kind === 'ok'
          ? { kind: 'ok', carts: prev.carts.filter((c) => c.cartId !== resolved.cartId) }
          : prev,
      );
      const variant: ToastVariant =
        code === 'Adverse-Action' ? 'warn' : code === 'Processed' ? 'ok' : 'info';
      setToast({
        variant,
        message:
          code === 'Adverse-Action'
            ? 'Cart closed as Adverse Action — added to compliance queue.'
            : code === 'Processed'
              ? 'Cart closed as Processed.'
              : `Cart closed as ${code}.`,
      });
    },
    [],
  );

  const handleSwept = useCallback((res: SweepEmptyFsrDraftsResponse): void => {
    const ids = new Set<string>(res.resolvedCartIds ?? []);
    setState((prev) =>
      prev.kind === 'ok'
        ? { kind: 'ok', carts: prev.carts.filter((c) => !ids.has(c.cartId)) }
        : prev,
    );
    setToast({
      variant: 'ok',
      message: `Swept ${res.sweptCount} empty draft${res.sweptCount === 1 ? '' : 's'}.`,
    });
  }, []);

  const handleRetry = (): void => {
    setState({ kind: 'loading' });
    const signal = makeSignal();
    void client.retail.carts.listMy(undefined, { signal }).then(
      (res): void => {
        const anyRes = res as unknown as { success?: boolean; data?: CartDto[]; error?: string };
        if (anyRes?.success === false) {
          setState({ kind: 'error', message: anyRes.error ?? 'Unable to load carts.' });
          return;
        }
        setState({ kind: 'ok', carts: (anyRes.data ?? []) as ReadonlyArray<CartDto> });
      },
      (err: unknown): void => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setState({ kind: 'error', message: err instanceof Error ? err.message : 'Unable to load carts.' });
      },
    );
  };

  return (
    <div className="rao-landing">
      <header className="rao-landing__header">
        {/* PILOT 2026-09-11 (Bill): the "Retail Account Opening" title, the
            "Stage-gated, audit-first cart flow..." tagline AND the role pill /
            "Products loaded: N · entity rules: N" line are all gone. The tab is
            already labelled New Acct; the counts were developer telemetry. */}
        {/* Bill 2026-07-13 Item 4 — the header "+ New application" button was a
            duplicate of "+ Start a new application" in the body; competing
            duplicate buttons are removed app-wide. (The Wave-1 "Sweep empties"
            removal note from 2026-04-28 also lived here.) */}
      </header>

      {/* Online applications waiting to be worked — sits above the in-flight
          cart grid (same landing queue as the future customer-portal channel). */}
      <OnlineApplicationsPanel />

      {state.kind === 'loading' && (
        <InFlightCartGrid>
          <InFlightCartCardSkeleton />
          <InFlightCartCardSkeleton />
          <InFlightCartCardSkeleton />
        </InFlightCartGrid>
      )}

      {state.kind === 'error' && (
        <InFlightCartGrid>
          <div className="iflt-empty">
            <div className="iflt-empty__icon" aria-hidden>!</div>
            <h2 className="iflt-empty__title">We couldn't load your applications</h2>
            <p className="iflt-empty__msg">{state.message}</p>
            <div className="iflt-empty__actions">
              <Button variant="ghost" onClick={handleRetry}>
                Retry
              </Button>
              <Button variant="primary" onClick={() => navigate('/rao/new')}>
                + Start a new application
              </Button>
            </div>
          </div>
        </InFlightCartGrid>
      )}

      {state.kind === 'ok' && sortedCarts.length === 0 && (
        <InFlightCartGrid>
          <div className="iflt-empty">
            <div className="iflt-empty__icon" aria-hidden>+</div>
            <h2 className="iflt-empty__title">No applications in flight</h2>
            <p className="iflt-empty__msg">
              When you start a new application or claim one from RLQ, it'll appear here until it's
              committed.
            </p>
            <Button variant="primary" onClick={() => navigate('/rao/new')}>
              + Start a new application
            </Button>
          </div>
        </InFlightCartGrid>
      )}

      {state.kind === 'ok' && sortedCarts.length > 0 && (
        <>
          {/* Bill 2026-09-11: rows like the Online applications panel, not
              cards - far more space efficient. */}
          <InFlightCartList>
            {sortedCarts.map((c) => (
              <InFlightCartRow
                key={c.cartId}
                cart={c}
                products={config.products}
                branches={config.branches}
                onOpen={handleOpen}
                onCloseCart={handleCloseCart}
                {...(canReassign ? { onReassign: handleReassign } : {})}
              />
            ))}
          </InFlightCartList>
          {/* The ONE create button (Bill 2026-07-13 Item 4) — below the
              online-applications + existing-carts sections. */}
          <div style={{ marginTop: 16 }}>
            <Button variant="primary" onClick={() => navigate('/rao/new')}>
              + Start a new application
            </Button>
          </div>
        </>
      )}

      {/* BACKLOG-1746: committed applications drop off the in-flight list, so the completion page
          (accounts opened + remaining tasks) had no way back in. The officer's last ten. */}
      {recent.length > 0 && (
        <section aria-label="Recently completed" style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: 'var(--pcb-blue-dark)', margin: '0 0 8px' }}>Recently completed</h2>
          <InFlightCartList>
            {recent.map((c) => (
              <InFlightCartRow
                key={c.cartId}
                cart={c}
                products={config.products}
                branches={config.branches}
                onOpen={handleOpen}
              />
            ))}
          </InFlightCartList>
        </section>
      )}

      {closeTarget && (
        <CloseCartModal
          open={true}
          cart={closeTarget}
          onClose={() => setCloseTarget(null)}
          onResolved={handleResolved}
        />
      )}

      {reassignTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Reassign cart"
          style={{
            position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15,23,42,0.45)',
          }}
          onClick={() => !reassignBusy && setReassignTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface-card)', borderRadius: 10, padding: '20px 22px',
              width: 'min(460px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            }}
          >
            <h3 style={{ margin: '0 0 6px', color: 'var(--pcb-blue-dark)', fontSize: 18 }}>Reassign this cart</h3>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--ink-600)', lineHeight: 1.5 }}>
              Hands the application to another officer. Their officer code and branch are
              taken from their own record, and the cart leaves your list.
            </p>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-600)', marginBottom: 4 }}>
              Receiving officer
            </label>
            <select
              autoFocus
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 14,
                border: '1px solid var(--surface-border)', borderRadius: 6, marginBottom: 10,
                background: 'var(--surface-card)',
              }}
            >
              <option value="">— select an officer —</option>
              {officers.map((o) => (
                <option key={o.officerCode} value={o.officerCode}>
                  {o.displayName} ({o.officerCode}){o.branchCode ? ` · Branch ${o.branchCode}` : ''}
                </option>
              ))}
            </select>
            {reassignErr && (
              <div style={{ color: 'var(--pcb-red)', fontSize: 12.5, marginBottom: 10 }}>{reassignErr}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setReassignTarget(null)}
                disabled={reassignBusy}
                style={{
                  padding: '8px 14px', fontSize: 14, borderRadius: 6,
                  border: '1px solid var(--pcb-blue)', background: 'var(--surface-card)', color: 'var(--pcb-blue)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitReassign()}
                disabled={reassignBusy || !reassignTo.trim()}
                style={{
                  padding: '8px 14px', fontSize: 14, borderRadius: 6, border: 'none',
                  background: reassignBusy || !reassignTo.trim() ? 'var(--ink-500)' : 'var(--brand-fill)',
                  color: 'var(--on-brand)', cursor: reassignBusy || !reassignTo.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {reassignBusy ? 'Reassigning…' : 'Reassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SweepEmptiesPopover
        open={sweepOpen}
        onClose={() => setSweepOpen(false)}
        onSwept={handleSwept}
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}
        >
          <Toast
            variant={toast.variant}
            message={toast.message}
            autoHideMs={4000}
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </div>
  );
}
